import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";
import { color, font, space, radius, shadow } from "../lib/mcmTokens";
import { ServingsStepper } from "./ServingsStepper";
import { totalTimeMin, difficultyLabel, findRecipe } from "../data/recipes";
import { scaleRecipe } from "../lib/recipeScaling";
import { applyPreferredUnit, useUnitPrefsVersion, DISPLAY_CONTEXT } from "../lib/unitPrefs";
import { useScheduledMeals } from "../lib/useScheduledMeals";
import { useUserRecipes } from "../lib/useUserRecipes";
import {
  pickDurationMin,
  formatMinutes,
  findOvernightStepIndex,
  getOvernightDurations,
  OVERNIGHT_DAY2_START_HOUR,
} from "../lib/calendarUtils";

// Meal slots — the structural-time axis the user wanted in place of
// (or alongside) raw HH:MM input. Default times per slot are
// reasonable middle-of-the-window picks; the user can still override
// via the TIME input below. Persisted on scheduled_meals.meal_slot
// (migration 0069) so Plan can surface 'DINNER · Thu 6:30 PM' instead
// of just 'Thu 6:30 PM'.
const MEAL_SLOTS = [
  { id: "breakfast", label: "Breakfast", emoji: "🥞", defaultTime: "08:00" },
  { id: "lunch",     label: "Lunch",     emoji: "🥪", defaultTime: "12:30" },
  { id: "dinner",    label: "Dinner",    emoji: "🍽️", defaultTime: "18:30" },
];
// Snack is NOT in the day-strip grid (keeps the grid to 3 clean rows).
// Users who want to schedule a snack can pick via the optional time
// override — snack falls out naturally from inferSlotFromTime. If
// this becomes a frequent ask we expand the grid to 4 rows.
const SNACK_SLOT = { id: "snack", label: "Snack", emoji: "🍎", defaultTime: "15:00" };
const ALL_SLOTS = [...MEAL_SLOTS, SNACK_SLOT];

// Infer a meal slot from an HH:MM string so the chip row lights up
// correctly when the scheduler opens with a pre-set time.
function inferSlotFromTime(timeStr) {
  const [h] = (timeStr || "").split(":").map(Number);
  if (!Number.isFinite(h)) return "dinner";
  if (h < 10)  return "breakfast";
  if (h < 14)  return "lunch";
  if (h < 17)  return "snack";
  return "dinner";
}

// Local-tz date key "YYYY-MM-DD" for bucketing scheduled meals.
// Same shape the nutrition tally uses so consumers agree on "today."
function dayKey(d) {
  const dt = d instanceof Date ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Infer the slot for an existing scheduled_meals row. Prefers the
// explicit meal_slot column (migration 0069), falls back to a
// time-based inference for pre-0069 rows so the grid still renders
// them in the right row.
function slotForMeal(meal) {
  if (meal?.meal_slot) return meal.meal_slot;
  if (!meal?.scheduled_for) return "dinner";
  const t = new Date(meal.scheduled_for);
  const hh = `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
  return inferSlotFromTime(hh);
}

// Format a JS Date as a local ISO-ish string for <input type="time"> ("HH:mm").
const hhmm = (d) =>
  `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

// Parse "HH:mm" into { hours, minutes }.
const parseHHMM = (s) => {
  const [h, m] = (s || "19:00").split(":").map(Number);
  return { hours: h || 0, minutes: m || 0 };
};

// Build the next N days starting at `start` (midnight local).
function buildDayStrip(start, count = 14) {
  const days = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    d.setHours(0, 0, 0, 0);
    days.push(d);
  }
  return days;
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * SchedulePicker modal.
 *
 * Props:
 *   recipe         — the recipe to schedule
 *   initialDate    — optional Date the picker should default to
 *   userId         — current user id (for self-cook option)
 *   userName       — current user's first name (for "Me (Alex)" label)
 *   family         — [{ otherId, other: { name } }] from useRelationships
 *   defaultRequest — if true, the "Who's cooking?" picker defaults to REQUEST
 *   onClose()      — user dismissed
 *   onSave({ scheduledFor, notificationSettings, note, cookId, isRequest, servings })
 */
export default function SchedulePicker({
  recipe, initialDate, initialSlot, userId, userName, family = [], friends = [], defaultRequest = false,
  onClose, onSave,
}) {
  // Re-render when the user flips US ↔ metric so the scaled-
  // ingredient preview tracks the preference (data-adaptability
  // rule: stored amounts stay source-verbatim, display transforms
  // here).
  useUnitPrefsVersion();
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const days = useMemo(() => buildDayStrip(today, 14), [today]);

  // Existing scheduled meals in the visible window — drives the
  // filled-slot preview on the day grid. Window = start of today
  // through end of day+13 (14-day strip). Using the hook here means
  // SchedulePicker re-renders when the user schedules a meal from a
  // different surface; the grid stays honest.
  const { meals: existingMeals } = useScheduledMeals(userId, {
    fromISO: useMemo(() => today.toISOString(), [today]),
    toISO:   useMemo(() => {
      const end = new Date(today);
      end.setDate(end.getDate() + 14);
      end.setHours(23, 59, 59, 999);
      return end.toISOString();
    }, [today]),
  });
  // Matches the Plan pattern: useUserRecipes exposes a findBySlug
  // helper that combines user-authored recipes with the bundled
  // library. findRecipe(slug, findBySlug) resolves either source.
  const { findBySlug: findUserRecipe } = useUserRecipes(userId);

  // { "YYYY-MM-DD": { breakfast, lunch, dinner, snack } }.
  // Each value is the existing meal row (or undefined if empty).
  // First-wins on collision — a duplicate slot on the same day is
  // rare but possible; we just surface the earliest-scheduled one.
  const byDaySlot = useMemo(() => {
    const out = {};
    for (const m of existingMeals || []) {
      const k = dayKey(m.scheduled_for);
      if (!out[k]) out[k] = {};
      const slot = slotForMeal(m);
      if (!out[k][slot]) out[k][slot] = m;
    }
    return out;
  }, [existingMeals]);

  const [selectedDay, setSelectedDay] = useState(() => {
    if (initialDate) {
      const d = new Date(initialDate);
      d.setHours(0, 0, 0, 0);
      return d;
    }
    return today;
  });
  // Meal slot drives the default time. Picking Dinner sets 6:30 PM;
  // picking Lunch sets 12:30 PM. User can still override the TIME
  // input to any HH:MM. Changing the time manually re-infers the
  // slot so the chip row always reflects which window they're in.
  // Seed slot + time from initialSlot when the caller pre-selected
  // one (e.g. Plan's "+ add to breakfast" tap on an empty slot row).
  // Falls back to dinner as the sensible default for users opening
  // the picker without a slot hint.
  const seededSlotId = (initialSlot && ALL_SLOTS.find(s => s.id === initialSlot))
    ? initialSlot
    : "dinner";
  const [mealSlot, setMealSlot] = useState(seededSlotId);
  const [timeStr,  setTimeStr]  = useState(() => {
    const slot = ALL_SLOTS.find(s => s.id === seededSlotId);
    return slot?.defaultTime || "18:30";
  });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Anchor toggle — "cook" means the picked time is when the user
  // starts cooking (legacy default); "eat" means it's when they want
  // to sit down with the meal and the cook is backdated. Long cooks
  // (≥90m) default to EAT — the question "when do I want to eat
  // brioche" is more natural than "when do I start a 9-hour proof."
  const recipeDuration = useMemo(() => pickDurationMin(recipe, 30), [recipe]);
  const [anchor, setAnchor] = useState(() => recipeDuration >= 90 ? "eat" : "cook");
  const overnightDur = useMemo(() => getOvernightDurations(recipe), [recipe]);
  const isOvernight  = overnightDur != null;
  // Per-meal Day 2 morning anchor for overnight bakes. Only meaningful
  // when cook-anchored — eat-anchored derives Day 2 from eat time, so
  // the override is hidden in that mode. Stored as "HH:MM" string for
  // the time input; written as integer hour to scheduled_meals.day2_hour.
  const [day2HourStr, setDay2HourStr] = useState(
    () => `${String(OVERNIGHT_DAY2_START_HOUR).padStart(2, "0")}:00`,
  );

  // When the user picks a slot chip, snap time to that slot's default.
  // When they manually change the TIME input, re-infer the slot.
  const pickSlot = (slotId) => {
    const slot = MEAL_SLOTS.find(s => s.id === slotId);
    if (!slot) return;
    setMealSlot(slotId);
    setTimeStr(slot.defaultTime);
  };
  const pickTime = (t) => {
    setTimeStr(t);
    setMealSlot(inferSlotFromTime(t));
  };

  // Cook picker. "request" = nobody assigned yet, userId = self, other uuid = family
  // member. Default to request per user spec ("start as request until changed").
  const [cookChoice, setCookChoice] = useState(defaultRequest ? "request" : (userId || "request"));

  // Team-cook participants (migration 0173). Lead = whoever is chosen
  // above. helperIds[] are co-cooks who can be assigned individual
  // steps; dinerIds[] are observers who get the live telemetry view
  // but can't take actions. Multi-select from family + friends.
  const [helperIds, setHelperIds] = useState(() => new Set());
  const [dinerIds, setDinerIds] = useState(() => new Set());
  const toggleHelper = (uid) => setHelperIds((prev) => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid);
    else { next.add(uid); }
    // A diner can't also be a helper. Promotion clears the other axis.
    setDinerIds((prevD) => {
      if (!prevD.has(uid)) return prevD;
      const nextD = new Set(prevD); nextD.delete(uid); return nextD;
    });
    return next;
  });
  const toggleDiner = (uid) => setDinerIds((prev) => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid);
    else { next.add(uid); }
    setHelperIds((prevH) => {
      if (!prevH.has(uid)) return prevH;
      const nextH = new Set(prevH); nextH.delete(uid); return nextH;
    });
    return next;
  });

  // Servings stepper. Seed from the recipe's default.
  const [servings, setServings] = useState(recipe.serves || 2);

  // Per-notification opt-in state, keyed by notification.id.
  const [notifOpts, setNotifOpts] = useState(() => {
    const map = {};
    for (const n of recipe.prepNotifications || []) {
      map[n.id] = n.defaultOn !== false;
    }
    return map;
  });

  const toggleNotif = (id) =>
    setNotifOpts(prev => ({ ...prev, [id]: !prev[id] }));

  // Cook options: [ {id: "request", label: "Request — ask family"} , {id: userId, label: "Me (…)"}, ...family ]
  const cookOptions = useMemo(() => {
    const options = [
      { id: "request", label: "🙋 Request", hint: "Ask family to volunteer" },
    ];
    if (userId) {
      options.push({ id: userId, label: userName ? `${userName} (me)` : "Me", hint: "I'll cook it" });
    }
    // Anyone you're connected to can be assigned the cook — family AND
    // friends. A friend (e.g. a step-dad invited as a friend, not yet
    // upgraded to family) shouldn't be barred from cooking. seen-guard
    // dedups in the unlikely event the same id appears twice.
    const seen = new Set(options.map((o) => o.id));
    const addPerson = (otherId, name) => {
      if (!otherId || seen.has(otherId)) return;
      const first = (name || "").trim().split(/\s+/)[0];
      if (!first) return;
      seen.add(otherId);
      options.push({ id: otherId, label: first, hint: "Assign to them" });
    };
    for (const f of family)  addPerson(f.otherId, f.other?.name);
    for (const f of friends) addPerson(f.otherId, f.other?.name);
    return options;
  }, [userId, userName, family, friends]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const { hours, minutes } = parseHHMM(timeStr);
      const dt = new Date(selectedDay);
      dt.setHours(hours, minutes, 0, 0);
      const isRequest = cookChoice === "request";
      // Day 2 hour override only matters for cook-anchored overnight
      // bakes. Eat-anchored derives Day 2 from eat time; non-overnight
      // recipes ignore the field entirely.
      const day2Hour = (isOvernight && anchor === "cook")
        ? parseHHMM(day2HourStr).hours
        : null;
      await onSave({
        scheduledFor: dt.toISOString(),
        notificationSettings: notifOpts,
        note: note.trim() || null,
        cookId: isRequest ? null : cookChoice,
        isRequest,
        servings,
        mealSlot,
        eatAnchor: anchor === "eat",
        day2Hour,
        helperIds: Array.from(helperIds),
        dinerIds:  Array.from(dinerIds),
      });
      onClose();
    } catch (e) {
      setError(e.message || "Couldn't save. Try again.");
      setSaving(false);
    }
  };

  // Portal — Plan.jsx mounts this from inside an App-level
  // <motion.div> with a transform, which makes `position: fixed`
  // resolve to the motion.div's bounds instead of the viewport.
  // Without the portal, the picker's inset:0 covers only the
  // calendar's content area and the panel anchors to the bottom
  // of the calendar, far below the viewport.
  return createPortal(
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={panelStyle}>
        <div style={dragHandleStyle} />

        {/* Recipe header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: space.flow, marginBottom: space.gap }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={kickerStyle}>SCHEDULE</div>
            <div style={titleStyle}>{recipe.title}</div>
            <div style={metaStyle}>
              {totalTimeMin(recipe)} min · {difficultyLabel(recipe.difficulty)}
              {recipe.cuisine ? ` · ${recipe.cuisine}` : ""}
            </div>
          </div>
        </div>

        {/* Day grid */}
        <div style={fieldLabelStyle}>WHEN</div>
        <div style={dayStripStyle}>
          {days.map(d => {
            const isSelectedDay = d.getTime() === selectedDay.getTime();
            const isToday = d.getTime() === today.getTime();
            const k = dayKey(d);
            const filled = byDaySlot[k] || {};
            // Compact "filled" indicator dot when ANY slot already has
            // a meal scheduled for this day. Replaces the three-tier
            // breakfast/lunch/dinner cell grid the user retired —
            // they don't think in slot rows anymore. The user picks
            // a day; TIME below picks the clock time.
            const hasAny = Object.values(filled).some(Boolean);
            return (
              <button
                key={d.toISOString()}
                onClick={() => setSelectedDay(d)}
                style={dayHeaderStyle(isSelectedDay)}
              >
                <div style={dayHeaderKickerStyle(isSelectedDay)}>
                  {isToday ? "TODAY" : DAY_LABELS[d.getDay()].toUpperCase()}
                </div>
                <div style={dayHeaderDateStyle(isSelectedDay)}>
                  {d.getDate()}
                </div>
                <div style={dayDotRowStyle}>
                  {hasAny && <span style={dayDotStyle(isSelectedDay)} />}
                </div>
              </button>
            );
          })}
        </div>

        {/* Selected target / time + anchor toggle */}
        <div style={targetCardStyle}>
          <div style={targetKickerStyle}>
            {DAY_LABELS[selectedDay.getDay()].toUpperCase()}, {selectedDay.toLocaleDateString(undefined, { month: "short", day: "numeric" }).toUpperCase()}
          </div>
          <div style={{ marginTop: space.inline, display: "flex", alignItems: "center", gap: space.tight }}>
            <span style={inlineLabelStyle}>TIME</span>
            <input
              type="time"
              value={timeStr}
              onChange={(e) => pickTime(e.target.value)}
              style={timeInputStyle}
            />
          </div>
          <div style={{ marginTop: space.inline, display: "flex", alignItems: "center", gap: space.tight }}>
            <span style={inlineLabelStyle}>I'M PICKING</span>
            <div style={{ display: "flex", gap: 4, flex: 1 }}>
              <button onClick={() => setAnchor("cook")} style={anchorPillStyle(anchor === "cook")}>
                Cook start
              </button>
              <button onClick={() => setAnchor("eat")} style={anchorPillStyle(anchor === "eat")}>
                Eat time
              </button>
            </div>
          </div>
          {anchor === "eat" && recipeDuration > 0 && !isOvernight && (() => {
            const { hours, minutes } = parseHHMM(timeStr);
            const eatDt = new Date(selectedDay);
            eatDt.setHours(hours, minutes, 0, 0);
            const cookStart = new Date(eatDt.getTime() - recipeDuration * 60_000);
            const sameDay = cookStart.toDateString() === eatDt.toDateString();
            const dayLabel = sameDay
              ? "today"
              : `${DAY_LABELS[cookStart.getDay()]}, ${cookStart.getDate()}`;
            const timeLabel = cookStart.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
            return (
              <div style={cookStartPreviewStyle}>
                Start cooking <b style={{ color: color.warmBrown, fontWeight: 600 }}>{dayLabel} at {timeLabel}</b>
                <span style={{ color: color.inkFaint }}> · {formatMinutes(recipeDuration)} total</span>
              </div>
            );
          })()}
          {/* OVERNIGHT BAKE PREVIEW — two-day picture. The user is
              picking either the Day 1 cook start or the Day 2 eat
              moment; we show the OPPOSITE end so they can see what
              they're committing to. Cook-anchored: also surfaces a
              Day 2 hour override (defaults to 8 AM). Eat-anchored:
              Day 2 derives from eat time, no override needed. */}
          {isOvernight && (() => {
            const { hours, minutes } = parseHHMM(timeStr);
            const pickedDt = new Date(selectedDay);
            pickedDt.setHours(hours, minutes, 0, 0);
            const totalRecipeMin = overnightDur.day1ActiveMin + overnightDur.overnightMin + overnightDur.day2Min;
            const fmtClock = (d) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
            const fmtDay = (d) => {
              const sameAsPicked = d.toDateString() === pickedDt.toDateString();
              return sameAsPicked ? "today" : `${DAY_LABELS[d.getDay()]}`;
            };
            let day1Start, day2Start, day2End;
            if (anchor === "eat") {
              day2End   = pickedDt;
              day2Start = new Date(pickedDt.getTime() - overnightDur.day2Min * 60_000);
              day1Start = new Date(pickedDt.getTime() - totalRecipeMin * 60_000);
            } else {
              day1Start = pickedDt;
              const day2H = parseHHMM(day2HourStr).hours;
              const day2M = parseHHMM(day2HourStr).minutes;
              const d = new Date(pickedDt);
              d.setDate(d.getDate() + 1);
              d.setHours(day2H, day2M, 0, 0);
              day2Start = d;
              day2End   = new Date(day2Start.getTime() + overnightDur.day2Min * 60_000);
            }
            return (
              <>
                <div style={overnightBadgeStyle}>
                  <span style={overnightBadgeChip}>SPANS TWO DAYS</span>
                  <span style={{ color: color.inkFaint, fontFamily: font.mono, fontSize: 10, letterSpacing: "0.06em" }}>
                    {formatMinutes(overnightDur.day1ActiveMin)} prep · {formatMinutes(overnightDur.overnightMin)} ferment · {formatMinutes(overnightDur.day2Min)} bake
                  </span>
                </div>
                <div style={cookStartPreviewStyle}>
                  Day 1 prep <b style={{ color: color.warmBrown, fontWeight: 600 }}>{fmtDay(day1Start)} {fmtClock(day1Start)}</b>
                </div>
                <div style={cookStartPreviewStyle}>
                  Day 2 bake <b style={{ color: color.warmBrown, fontWeight: 600 }}>{fmtDay(day2Start)} {fmtClock(day2Start)} → {fmtClock(day2End)}</b>
                </div>
                {anchor === "cook" && (
                  <div style={{ marginTop: space.inline, display: "flex", alignItems: "center", gap: space.tight }}>
                    <span style={inlineLabelStyle}>DAY 2 STARTS</span>
                    <input
                      type="time"
                      value={day2HourStr}
                      onChange={(e) => setDay2HourStr(e.target.value)}
                      style={timeInputStyle}
                    />
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Who's cooking? — request + me pinned; everyone you're
            connected to is searchable once the roster grows. */}
        <PeopleField
          label="WHO'S COOKING?"
          people={cookOptions.map((o) => ({
            id: o.id,
            label: o.label.replace(/^🙋\s*/, ""),
            pinned: o.id === "request" || o.id === userId,
          }))}
          selected={(id) => cookChoice === id}
          onPick={setCookChoice}
          hint={
            cookChoice === "request"
              ? "Family will see this as a request. Anyone can claim it."
              : cookChoice === userId
              ? "You'll get the prep reminders."
              : "They'll be tagged as the cook."
          }
        />

        {/* Team cooks — helpers + diners (migration 0173). Multi-select
            from family + friends. Helpers get assignable steps; diners
            get the read-only TEAM PROGRESS view in CookMode. */}
        {(family.length + friends.length) > 0 && (() => {
          const teammates = [];
          const seen = new Set();
          const push = (rel, isFamily) => {
            if (!rel.otherId || !rel.other?.name) return;
            if (seen.has(rel.otherId)) return;
            if (rel.otherId === userId) return;
            seen.add(rel.otherId);
            teammates.push({
              id: rel.otherId,
              name: rel.other.name.trim().split(/\s+/)[0],
              isFamily,
            });
          };
          for (const f of family) push(f, true);
          for (const f of friends) push(f, false);
          if (teammates.length === 0) return null;
          const teamPeople = teammates.map((t) => ({
            id: t.id,
            label: `${t.name}${t.isFamily ? "" : " *"}`,
          }));
          return (
            <>
              <PeopleField
                label="COOKS (HELPERS)"
                people={teamPeople}
                selected={(id) => helperIds.has(id)}
                onPick={toggleHelper}
                hint="Helpers can be assigned individual steps with deadlines."
              />
              <PeopleField
                label="DINERS (WATCHING)"
                people={teamPeople}
                selected={(id) => dinerIds.has(id)}
                onPick={toggleDiner}
                hint="Diners get the live cook view but can't take actions."
              />
            </>
          );
        })()}

        {/* Servings stepper */}
        <div style={{ marginTop: space.gap }}>
          <div style={fieldLabelStyle}>HOW MANY PEOPLE EATING?</div>
          <div style={servingsStepperStyle}>
            <ServingsStepper
              value={servings}
              onChange={(n) => setServings(n)}
              palette={{
                surface: color.paper, btnBg: color.paper, btnBorder: color.hairline,
                accent: color.warmBrown, accentSoft: "rgba(122,78,45,0.12)",
                ink: color.ink, muted: color.inkFaint,
                fontLabel: font.mono, fontNum: font.serif,
              }}
            />
          </div>
          {recipe.serves && servings !== recipe.serves && (() => {
            const scaled = scaleRecipe(recipe, servings);
            const scaledIngs = Array.isArray(scaled.ingredients) ? scaled.ingredients : [];
            if (!scaledIngs.length) return null;
            return (
              <div style={scaledCardStyle}>
                <div style={scaledKickerStyle}>SCALED FOR {servings} · from {recipe.serves}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {scaledIngs.slice(0, 8).map((ing, i) => (
                    <div key={i} style={scaledRowStyle}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ing.name}</span>
                      <span style={{ color: color.warmBrown, fontFamily: font.sans, fontWeight: 600, fontSize: 12, flexShrink: 0 }}>{ing.amount ? (applyPreferredUnit(ing.amount, ing, DISPLAY_CONTEXT.COOK) || ing.amount) : ""}</span>
                    </div>
                  ))}
                  {scaledIngs.length > 8 && (
                    <div style={{ fontFamily: font.sans, fontSize: 10, color: color.inkFaint, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600, marginTop: 4 }}>
                      + {scaledIngs.length - 8} MORE
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Prep notifications */}
        {(recipe.prepNotifications || []).length > 0 && (
          <div style={{ marginTop: space.gap }}>
            <div style={fieldLabelStyle}>PREP REMINDERS</div>
            <div style={{ display: "flex", flexDirection: "column", gap: space.tight }}>
              {recipe.prepNotifications.map(n => {
                const on = notifOpts[n.id];
                return (
                  <button
                    key={n.id}
                    onClick={() => toggleNotif(n.id)}
                    style={notifRowStyle(on)}
                  >
                    <div style={notifSwitchOuterStyle(on)}>
                      <div style={notifSwitchKnobStyle(on)} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: font.sans, fontSize: 13, fontWeight: 500, color: color.ink, lineHeight: 1.4 }}>
                        {n.text}
                      </div>
                      <div style={{ fontFamily: font.sans, fontSize: 10, color: color.inkFaint, marginTop: 2, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600 }}>
                        {n.leadTime} before
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Optional note */}
        <div style={{ marginTop: space.gap }}>
          <div style={fieldLabelStyle}>NOTE (OPTIONAL)</div>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="For Sarah's birthday, try with 'nduja…"
            style={noteInputStyle}
          />
        </div>

        {error && (
          <div style={errorStyle}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: space.tight, marginTop: space.gap }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={cancelBtnStyle}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={saveBtnStyle}
          >
            {saving ? "Scheduling…" : "Schedule it ›"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// === MCM styles ===================================================
// Modern editorial drawer — cream parchment, Forma DJR Deck for
// labels and actions, New Kansas italic for the recipe title, italic
// Fraunces for date display, mustard / teal / burnt accents only
// where they earn it. Mirrors the MealDetailDrawer vocabulary so the
// two surfaces read as one design system.

const overlayStyle = {
  position: "fixed", inset: 0,
  background: "rgba(30,24,16,0.55)",
  backdropFilter: "blur(4px)",
  WebkitBackdropFilter: "blur(4px)",
  // Above every modal in the app — Recipe Book (320), CookComplete
  // (9000), ShopModeCheckout (9500). Picker is always launched FROM
  // some other surface, so it must always layer on top.
  zIndex: 9999,
  display: "flex", alignItems: "flex-end", justifyContent: "center",
  padding: "0 0 max(0px, env(safe-area-inset-bottom))",
};

const panelStyle = {
  width: "100%",
  maxWidth: 520,
  maxHeight: "92vh",
  background: color.paper,
  borderTopLeftRadius: radius.sheet,
  borderTopRightRadius: radius.sheet,
  boxShadow: shadow.lift,
  padding: `${space.inline}px ${space.gap}px ${space.gap}px`,
  overflowY: "auto",
};

const dragHandleStyle = {
  width: 40, height: 4,
  background: color.hairline,
  borderRadius: radius.pill,
  margin: `0 auto ${space.flow}px`,
};

// Kicker — Forma DJR Deck weight 600 with uppercase tracking. The
// mono face read as dated next to the New Kansas italic title; same
// uppercase rhythm but in the geometric sans face that the rest of
// the app's labels share. Extra bottom-margin so the title's italic
// ascender doesn't crowd the kicker baseline.
const kickerStyle = {
  fontFamily: font.sans,
  fontSize: 10,
  color: color.mustard,
  letterSpacing: "0.16em",
  fontWeight: 600,
  textTransform: "uppercase",
  marginBottom: space.flow,
};

const titleStyle = {
  fontFamily: font.itemTitle,
  fontStyle: "italic",
  fontWeight: 500,
  fontSize: 26,
  color: color.ink,
  // New Kansas italic carries tall ascenders that overshoot a tight
  // line-height. 1.25 gives the B/L/k their full height plus a hair
  // of breath above; combined with the kicker's bottom-margin the
  // first line clears the SCHEDULE label without clipping. Same
  // reason the WebkitLineClamp/overflow:hidden combo isn't safe at
  // 1.1 — the box trims ascenders that fall outside the line.
  lineHeight: 1.25,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  overflowWrap: "anywhere",
  letterSpacing: "-0.01em",
  paddingTop: 4,
};

const metaStyle = {
  fontFamily: font.sans,
  fontSize: 12,
  color: color.inkFaint,
  marginTop: space.nudge,
  letterSpacing: "0.01em",
};

const fieldLabelStyle = {
  fontFamily: font.sans,
  fontSize: 10,
  color: color.inkFaint,
  letterSpacing: "0.16em",
  fontWeight: 600,
  textTransform: "uppercase",
  marginBottom: space.inline,
};

const inlineLabelStyle = {
  fontFamily: font.sans,
  fontSize: 10,
  color: color.inkFaint,
  letterSpacing: "0.14em",
  fontWeight: 600,
  textTransform: "uppercase",
  flexShrink: 0,
};

const dayStripStyle = {
  display: "flex",
  gap: space.tight,
  overflowX: "auto",
  paddingBottom: 4,
  scrollbarWidth: "none",
  WebkitOverflowScrolling: "touch",
};

const dayHeaderStyle = (selected) => ({
  flexShrink: 0,
  width: 60,
  padding: `${space.tight}px 0`,
  background: selected ? color.mustardTint : color.glassFillHeavy,
  border: `1px solid ${selected ? "rgba(212,166,55,0.55)" : color.hairline}`,
  borderRadius: radius.field,
  cursor: "pointer",
  boxShadow: selected ? "none" : shadow.soft,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2,
});

const dayHeaderKickerStyle = (selected) => ({
  fontFamily: font.sans,
  fontSize: 9,
  color: selected ? color.warmBrown : color.inkFaint,
  letterSpacing: "0.14em",
  fontWeight: 600,
  textTransform: "uppercase",
});

const dayHeaderDateStyle = (selected) => ({
  fontFamily: font.itemTitle,
  fontStyle: "italic",
  fontSize: 18,
  fontWeight: 500,
  color: selected ? color.warmBrown : color.ink,
  marginTop: 0,
  lineHeight: 1.1,
});

// Day "has any scheduled meal" indicator dot. Replaces the legacy
// breakfast/lunch/dinner cell grid. Selected day uses warm brown so
// the dot sits inside the mustard-tint without disappearing.
const dayDotRowStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: 6,
  marginTop: 4,
};

const dayDotStyle = (selected) => ({
  width: 4, height: 4,
  borderRadius: "50%",
  background: selected ? color.warmBrown : color.mustard,
});

const targetCardStyle = {
  marginTop: space.flow,
  padding: `${space.flow}px ${space.flow}px`,
  background: color.glassFillHeavy,
  border: `1px solid ${color.hairline}`,
  borderRadius: radius.card,
  boxShadow: shadow.soft,
};

const targetKickerStyle = {
  fontFamily: font.sans,
  fontSize: 11,
  color: color.mustard,
  letterSpacing: "0.16em",
  fontWeight: 600,
  textTransform: "uppercase",
};

const timeInputStyle = {
  flex: 1,
  padding: `${space.tight}px ${space.inline}px`,
  background: color.paper,
  border: `1px solid ${color.hairline}`,
  borderRadius: radius.chip,
  color: color.ink,
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 14,
  outline: "none",
  colorScheme: "light",
  boxShadow: shadow.inputInset,
};

const anchorPillStyle = (active) => ({
  flex: 1,
  padding: `${space.tight}px ${space.inline}px`,
  background: active ? color.mustardTint : color.paper,
  border: `1px solid ${active ? "rgba(212,166,55,0.55)" : color.hairline}`,
  color: active ? color.warmBrown : color.inkMuted,
  borderRadius: radius.pill,
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: 12,
  letterSpacing: "0.02em",
  cursor: active ? "default" : "pointer",
  whiteSpace: "nowrap",
});

const cookStartPreviewStyle = {
  marginTop: space.inline,
  padding: `${space.tight}px ${space.inline}px`,
  background: color.mustardTint,
  border: `1px solid rgba(212,166,55,0.42)`,
  borderRadius: radius.field,
  fontFamily: font.sans,
  fontSize: 12,
  color: color.inkMuted,
  lineHeight: 1.4,
};

const overnightBadgeStyle = {
  marginTop: space.inline,
  display: "flex",
  alignItems: "center",
  gap: space.tight,
  flexWrap: "wrap",
};

const overnightBadgeChip = {
  fontFamily: font.mono,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.14em",
  color: "#7A5BA1",
  background: "rgba(122,91,161,0.12)",
  border: "1px solid rgba(122,91,161,0.42)",
  padding: "3px 8px",
  borderRadius: 4,
};

const cookChipStyle = (selected, isReq) => ({
  padding: `${space.tight}px ${space.flow}px`,
  background: selected ? (isReq ? color.burntTint : color.tealTint)
    : color.glassFillHeavy,
  border: `1px solid ${
    selected ? (isReq ? "rgba(217,107,43,0.45)" : color.aqua)
    : color.hairline
  }`,
  color: selected ? (isReq ? color.burnt : color.teal) : color.inkMuted,
  borderRadius: radius.pill,
  fontFamily: font.serif,
  fontStyle: "italic",
  fontWeight: 500,
  fontSize: 14,
  cursor: "pointer",
  whiteSpace: "nowrap",
  boxShadow: selected ? "none" : shadow.soft,
});

const peopleSearchStyle = {
  width: "100%",
  boxSizing: "border-box",
  marginBottom: space.tight,
  padding: `${space.tight}px ${space.inline}px`,
  background: color.paper,
  border: `1px solid ${color.hairline}`,
  borderRadius: radius.chip,
  color: color.ink,
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 14,
  outline: "none",
  colorScheme: "light",
  boxShadow: shadow.inputInset,
};

// Searchable, taggable people row. Renders a label, a name filter (only
// once the list is long enough to be worth searching), and the matching
// chips. `pinned` options (Request / Me) and already-selected people
// always stay visible regardless of the query, so the user never loses
// their current pick behind the filter. Used for Who's-cooking (single-
// select) and Helpers / Diners (multi-select).
function PeopleField({ label, people, selected, onPick, hint }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const searchable = people.filter((p) => !p.pinned);
  const showSearch = searchable.length > 4;
  const visible = people.filter(
    (p) => p.pinned || selected(p.id) || !query || p.label.toLowerCase().includes(query),
  );
  const noMatch = showSearch && query && visible.every((p) => p.pinned || selected(p.id));
  return (
    <div style={{ marginTop: space.gap }}>
      <div style={fieldLabelStyle}>{label}</div>
      {showSearch && (
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name…"
          aria-label={`Search ${label}`}
          autoCapitalize="none"
          autoCorrect="off"
          style={peopleSearchStyle}
        />
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: space.tight }}>
        {visible.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPick(p.id)}
            style={cookChipStyle(selected(p.id), p.id === "request")}
          >
            {p.label}
          </button>
        ))}
        {noMatch && (
          <span style={{ ...cookHintStyle, marginTop: 0, alignSelf: "center" }}>
            No match for “{q.trim()}”.
          </span>
        )}
      </div>
      {hint && <div style={cookHintStyle}>{hint}</div>}
    </div>
  );
}

const cookHintStyle = {
  marginTop: space.tight,
  fontFamily: font.sans,
  fontSize: 11,
  color: color.inkFaint,
  lineHeight: 1.5,
};

const servingsStepperStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: space.flow,
  background: color.glassFillHeavy,
  border: `1px solid ${color.hairline}`,
  borderRadius: radius.pill,
  padding: `${space.tight}px ${space.flow}px`,
  boxShadow: shadow.soft,
};

const servingsBtnStyle = (disabled) => ({
  width: 40, height: 40,
  borderRadius: radius.pill,
  background: color.paper,
  border: `1px solid ${color.hairline}`,
  color: disabled ? color.hairline : color.warmBrown,
  fontFamily: font.serif,
  fontSize: 18, lineHeight: 1, padding: 0,
  cursor: disabled ? "not-allowed" : "pointer",
});

const servingsCountStyle = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  // Italic numerals carry asymmetric optical weight (the 7's descender
  // sits below the baseline). Match the buttons' height (40px) so
  // the digit centers in the same vertical box, then nudge up by 1px
  // to compensate for italic 7/4/9's bottom-right bias against the
  // round button glyphs.
  height: 40,
  paddingBottom: 2,
  fontFamily: font.itemTitle,
  fontStyle: "italic",
  fontSize: 28,
  color: color.ink,
  fontWeight: 500,
  lineHeight: 1,
};

const servingsLabelStyle = {
  fontFamily: font.sans,
  fontSize: 10,
  color: color.inkFaint,
  letterSpacing: "0.16em",
  fontWeight: 600,
  textTransform: "uppercase",
  marginTop: 2,
};

const scaledCardStyle = {
  marginTop: space.inline,
  padding: `${space.inline}px ${space.flow}px`,
  background: color.paper,
  border: `1px solid ${color.hairline}`,
  borderRadius: radius.field,
};

const scaledKickerStyle = {
  fontFamily: font.sans,
  fontSize: 10,
  color: color.inkFaint,
  letterSpacing: "0.16em",
  fontWeight: 600,
  textTransform: "uppercase",
  marginBottom: space.tight,
};

const scaledRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: space.inline,
  fontFamily: font.sans,
  fontSize: 12,
  color: color.inkMuted,
};

const notifRowStyle = (on) => ({
  textAlign: "left",
  display: "flex",
  alignItems: "center",
  gap: space.flow,
  padding: `${space.inline}px ${space.flow}px`,
  background: on ? color.mustardTint : color.glassFillHeavy,
  border: `1px solid ${on ? "rgba(212,166,55,0.42)" : color.hairline}`,
  borderRadius: radius.field,
  cursor: "pointer",
  boxShadow: on ? "none" : shadow.soft,
});

const notifSwitchOuterStyle = (on) => ({
  width: 34, height: 20, borderRadius: radius.pill,
  background: on ? color.mustard : color.hairline,
  flexShrink: 0,
  position: "relative",
  transition: "background 200ms ease",
});

const notifSwitchKnobStyle = (on) => ({
  position: "absolute",
  top: 2,
  left: on ? 16 : 2,
  width: 16, height: 16,
  borderRadius: "50%",
  background: color.paper,
  transition: "left 200ms ease",
  boxShadow: "0 1px 2px rgba(30,30,30,0.18)",
});

const noteInputStyle = {
  width: "100%",
  padding: `${space.flow}px ${space.flow}px`,
  background: color.paper,
  border: `1px solid ${color.hairline}`,
  borderRadius: radius.field,
  color: color.ink,
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 14,
  outline: "none",
  boxShadow: shadow.inputInset,
};

const errorStyle = {
  marginTop: space.inline,
  padding: `${space.inline}px ${space.flow}px`,
  background: "rgba(217,107,43,0.10)",
  border: "1px solid rgba(217,107,43,0.45)",
  borderRadius: radius.field,
  fontFamily: font.sans,
  fontSize: 13,
  color: color.burnt,
};

const baseActionBtn = {
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: 14,
  letterSpacing: "0.02em",
  borderRadius: radius.pill,
  padding: `${space.flow}px ${space.gap}px`,
  cursor: "pointer",
};

const cancelBtnStyle = {
  ...baseActionBtn,
  flex: 1,
  background: color.glassFillHeavy,
  border: `1px solid ${color.hairline}`,
  color: color.inkMuted,
};

const saveBtnStyle = {
  ...baseActionBtn,
  flex: 2,
  background: "linear-gradient(180deg, #C85A1F 0%, #A34711 100%)",
  border: "1px solid rgba(255,255,255,0.35)",
  color: "#FFF8EE",
  fontSize: 15,
  letterSpacing: "0.04em",
  boxShadow: shadow.cta,
};
